import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download,
  ExternalLink,
  Film,
  GalleryHorizontal,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  ScanSearch,
  X,
} from 'lucide-react';
import { apiPost } from '../../utils/apiAdapter';
import { API_HOST } from '../../utils/constants';
import {
  formatMediaDuration,
  isVideoResource,
  sortResourcesByDateDesc,
} from '../resource/resourceViewUtils';
import PixelStage from './PixelStage';
import { buildResourceGalleryScene, MAX_GALLERY_ARTWORKS } from './resourceGalleryScene';

const galleryResourceId = (resource, index) => String(resource?.id ?? `gallery-${index}`);

function ResourcePreview({ resource, onClose }) {
  if (!resource) return null;
  const isVideo = isVideoResource(resource);
  const sourceUrl = resource.url || resource.thumbnailUrl;
  const duration = formatMediaDuration(resource.duration);
  const resourceType = resource.sourceLabel || resource.typeLabel || (isVideo ? '视频' : '图片');

  return (
    <div
      className="pixel-town-gallery-preview"
      role="dialog"
      aria-modal="true"
      aria-label={`调查作品：${resource.name || '未命名资源'}`}
      data-pixel-town-ignore-keys="true"
    >
      <button
        type="button"
        className="pixel-town-gallery-preview__scrim"
        aria-label="关闭预览"
        onClick={onClose}
      />
      <section className="pixel-town-gallery-preview__panel">
        <header className="pixel-town-gallery-preview__header">
          <div className="pixel-town-gallery-preview__identity">
            <span className="pixel-town-gallery-preview__eyebrow">
              <ScanSearch size={14} />
              作品调查
            </span>
            <strong>{resource.name || '未命名资源'}</strong>
            <span>
              {resourceType}
              {duration ? ` · ${duration}` : ''}
            </span>
          </div>
          <div className="pixel-town-gallery-preview__actions">
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                download
                className="pixel-town-gallery__icon-button"
                title="下载资源"
                aria-label="下载资源"
              >
                <Download size={16} />
              </a>
            )}
            <button
              type="button"
              className="pixel-town-gallery__icon-button"
              onClick={onClose}
              title="关闭预览"
              aria-label="关闭预览"
            >
              <X size={17} />
            </button>
          </div>
        </header>

        <div className="pixel-town-gallery-preview__body">
          <div className="pixel-town-gallery-preview__media">
            {sourceUrl ? (
              isVideo ? (
                <video src={sourceUrl} controls playsInline preload="metadata" />
              ) : (
                <img src={sourceUrl} alt={resource.name || ''} decoding="async" />
              )
            ) : (
              <div className="pixel-town-gallery-preview__empty">
                {isVideo ? <Film size={38} /> : <ImageIcon size={38} />}
                <span>资源暂时无法预览</span>
              </div>
            )}
          </div>

          <aside className="pixel-town-gallery-preview__inspection">
            <div className="pixel-town-gallery-preview__inspection-title">
              <span>观察记录</span>
              <small>{resourceType}</small>
            </div>
            <p>
              {resource.prompt?.trim()
                || (isVideo ? '一段已收录的视频作品，可以播放检查画面与节奏。' : '一张已收录的视觉作品，可以放大检查构图与细节。')}
            </p>
            <dl>
              <div>
                <dt>媒介</dt>
                <dd>{resourceType}</dd>
              </div>
              {duration && (
                <div>
                  <dt>时长</dt>
                  <dd>{duration}</dd>
                </div>
              )}
              <div>
                <dt>状态</dt>
                <dd>已收录</dd>
              </div>
            </dl>
          </aside>
        </div>

        <footer className="pixel-town-gallery-preview__footer">
          <span><kbd>Esc</kbd> 返回走廊</span>
          {sourceUrl && (
            <a href={sourceUrl} target="_blank" rel="noreferrer" download>
              <Download size={15} />
              下载原件
            </a>
          )}
        </footer>
      </section>
    </div>
  );
}

export default function TownResourceGallery({
  projectId,
  projectName,
  avatar,
  onCustomizeAvatar,
  onClose,
  onOpenResourceCenter,
}) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [previewResource, setPreviewResource] = useState(null);

  const loadResources = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await apiPost(`${API_HOST}/api/resource/query`, {
        projectId: Number(projectId),
      });
      const uniqueResources = Array.from(
        new Map((Array.isArray(data) ? data : []).map((item, index) => (
          [galleryResourceId(item, index), item]
        ))).values(),
      );
      setResources(sortResourcesByDateDesc(uniqueResources));
    } catch (error) {
      console.error('[pixel-office] 资源画廊加载失败', error);
      setLoadError(error?.message || '资源画廊暂时无法打开');
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  useEffect(() => {
    if (!previewResource) return undefined;
    const closePreviewOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      setPreviewResource(null);
    };
    window.addEventListener('keydown', closePreviewOnEscape, true);
    return () => window.removeEventListener('keydown', closePreviewOnEscape, true);
  }, [previewResource]);

  const galleryState = loading ? 'loading' : loadError ? 'error' : 'ready';
  const galleryScene = useMemo(() => buildResourceGalleryScene({
    projectId,
    projectName,
    resources,
    state: galleryState,
  }), [galleryState, projectId, projectName, resources]);

  const resourceIndex = useMemo(() => new Map(
    resources.map((resource, index) => [galleryResourceId(resource, index), resource]),
  ), [resources]);

  const welcomeToasts = useMemo(() => (loading ? [] : [
    '欢迎来到横向资源长廊，方向键、WASD 或点击地面都可以左右移动',
    '靠近作品后点击“调查作品”，原图或视频只会在调查页中加载',
  ]), [loading]);

  const handleAction = useCallback((action) => {
    if (action?.type !== 'custom') return;
    if (action.id === 'gallery-resource') {
      const resource = resourceIndex.get(String(action.resourceId));
      if (resource) setPreviewResource(resource);
      return;
    }
    if (action.id === 'gallery-resource-center') {
      onOpenResourceCenter?.();
      return;
    }
    if (action.id === 'gallery-exit') onClose?.();
  }, [onClose, onOpenResourceCenter, resourceIndex]);

  return (
    <div className="pixel-town-gallery-scene">
      <PixelStage
        scene={galleryScene}
        avatar={avatar}
        paused={Boolean(previewResource)}
        welcomeToasts={welcomeToasts}
        onAction={handleAction}
        onEscape={onClose}
        onCustomizeAvatar={onCustomizeAvatar}
      />

      <div className="pixel-town-gallery-scene__hud" data-pixel-town-ignore-keys="true">
        <span className="pixel-town-gallery-scene__mark"><GalleryHorizontal size={16} /></span>
        <div className="pixel-town-gallery-scene__summary">
          <strong>资源画廊</strong>
          <span>
            {loading
              ? '正在布展'
              : loadError
                ? '布展中断'
                : `${Math.min(resources.length, MAX_GALLERY_ARTWORKS)} / ${resources.length} 件在展`}
          </span>
        </div>
        {loading && <Loader2 size={15} className="pixel-town-gallery__spinner" aria-label="正在加载" />}
        {loadError && (
          <button
            type="button"
            className="pixel-town-gallery-scene__action"
            onClick={loadResources}
            title="重新布展"
            aria-label="重新布展"
          >
            <RefreshCw size={15} />
          </button>
        )}
        <button
          type="button"
          className="pixel-town-gallery-scene__action"
          onClick={onOpenResourceCenter}
          title="打开完整资源中心"
          aria-label="打开完整资源中心"
        >
          <ExternalLink size={15} />
        </button>
        <button
          type="button"
          className="pixel-town-gallery-scene__action"
          onClick={onClose}
          title="离开资源画廊"
          aria-label="离开资源画廊"
        >
          <X size={16} />
        </button>
      </div>

      <ResourcePreview resource={previewResource} onClose={() => setPreviewResource(null)} />
    </div>
  );
}
